import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';
import { buildLocalStorageKey } from '@risksmart-app/components/src/hooks/useLocalStorage';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useCallback, useState } from 'react';

import { handleError } from '@/utils/errorUtils';

import { PAGE_SIZE_OPTIONS } from './tablePreferences';

/**
 * The Cloudscape preferences the CDS preferences modal can produce. The generic is
 * Cloudscape's `custom` slot, which this table does not use.
 */
export type CustomDatasourceTablePreferences =
  CollectionPreferencesProps.Preferences<unknown>;

/** Every surface's preferences in one blob, keyed by `scope` — as the widths store is. */
type PreferencesStore = Record<string, CustomDatasourceTablePreferences>;

// Cloudscape declares neither of these as an exported name of its own, so take them
// from the value type rather than restating the shapes here.
type StickyColumns = NonNullable<
  CustomDatasourceTablePreferences['stickyColumns']
>;
type ContentDensity = NonNullable<
  CustomDatasourceTablePreferences['contentDensity']
>;

const STORAGE_KEY = 'CustomDatasourceTablePreferences-PreferencesV1';

const CONTENT_DENSITIES: ContentDensity[] = ['comfortable', 'compact'];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readContentDisplay = (
  value: unknown
): CollectionPreferencesProps.ContentDisplayItem[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  // `visible` is required, and an entry missing it is not merely incomplete — the two
  // consumers read the gap differently. The table filters on truthiness and hides the
  // column; the exporter filters on `visible !== false` and writes it to the CSV. The
  // entry is rebuilt rather than kept, so nothing else in the payload rides through.
  const items = value.flatMap((item) =>
    isPlainObject(item) &&
    typeof item.id === 'string' &&
    typeof item.visible === 'boolean'
      ? [{ id: item.id, visible: item.visible }]
      : []
  );

  // A partially-readable list is worse than none: Cloudscape renders exactly the ids
  // in `columnDisplay`, so dropping a malformed entry would hide that column with no
  // way for the user to tell why.
  return items.length === value.length ? items : undefined;
};

const readStickyColumns = (value: unknown): StickyColumns | undefined => {
  if (!isPlainObject(value)) {
    return undefined;
  }
  // The modal only offers whole counts, so a negative or fractional one cannot have
  // come from it.
  const readCount = (count: unknown) =>
    typeof count === 'number' && Number.isInteger(count) && count >= 0
      ? count
      : undefined;
  const first = readCount(value.first);
  const last = readCount(value.last);

  return first === undefined && last === undefined
    ? undefined
    : {
        ...(first === undefined ? {} : { first }),
        ...(last === undefined ? {} : { last }),
      };
};

/**
 * Reads one scope's stored preferences, keeping only the values that are still
 * well-formed.
 *
 * Parsing without throwing is not the same as valid, and each of these reaches
 * something that assumes its type: a malformed `contentDisplay` goes to Cloudscape's
 * `columnDisplay` and takes the table down on render, and a `pageSize` that is not one
 * of the offered options becomes the reporting query's `limit` while the Page size
 * radio group shows nothing selected.
 *
 * Only the preferences this modal can produce are kept — anything else in the payload
 * is discarded rather than passed through, since nothing can legitimately have written
 * it.
 */
const readPreferences = (
  value: unknown
): CustomDatasourceTablePreferences | undefined => {
  if (!isPlainObject(value)) {
    return undefined;
  }

  const readBoolean = (flag: unknown) =>
    typeof flag === 'boolean' ? flag : undefined;

  return {
    pageSize:
      typeof value.pageSize === 'number' &&
      PAGE_SIZE_OPTIONS.includes(value.pageSize)
        ? value.pageSize
        : undefined,
    contentDensity: CONTENT_DENSITIES.find(
      (density) => density === value.contentDensity
    ),
    wrapLines: readBoolean(value.wrapLines),
    stripedRows: readBoolean(value.stripedRows),
    contentDisplay: readContentDisplay(value.contentDisplay),
    stickyColumns: readStickyColumns(value.stickyColumns),
  };
};

const readStore = (storageKey: string): PreferencesStore => {
  try {
    const persisted = window.localStorage.getItem(storageKey);
    if (!persisted) {
      return {};
    }

    const parsed: unknown = JSON.parse(persisted);
    if (!isPlainObject(parsed)) {
      return {};
    }

    // Null-prototype: the keys come from the parsed payload, and `JSON.parse` makes
    // `__proto__` an own enumerable property, so assigning it into an object literal
    // would re-point this store's prototype instead of adding an entry.
    const store: PreferencesStore = Object.create(null) as PreferencesStore;
    for (const [scopeKey, scopePreferences] of Object.entries(parsed)) {
      const preferences = readPreferences(scopePreferences);
      if (preferences) {
        store[scopeKey] = preferences;
      }
    }

    return store;
  } catch (error) {
    // Private mode, quota, or genuinely unparseable content.
    handleError(error);

    return {};
  }
};

/**
 * Per-user, per-instance persistence for a CDS table's column preferences — order,
 * visibility, page size and the display toggles.
 *
 * localStorage, and one blob keyed by `scope`, for the same reasons as
 * `useCustomDatasourceColumnWidths`: these are personal reading preferences rather
 * than properties of a datasource other people also open, a single registered
 * `LocalStorageKeys` entry cannot express a key per datasource, and merging against
 * the live record at write time keeps concurrently-mounted tables from erasing each
 * other. Build `scope` with that hook's `buildColumnWidthScope` so a surface's widths
 * and its column preferences are remembered against the same instance.
 *
 * Unlike widths, these drive rendering and the reporting query's page size, so the
 * value is held in React state too — the caller re-renders on change. A `scope` of
 * `undefined` (a datasource with no id yet) makes the hook state-only: preferences
 * still apply, they just have nowhere stable to belong.
 */
export const useCustomDatasourceTablePreferences = (
  scope: string | undefined
): [
  CustomDatasourceTablePreferences | undefined,
  (preferences: CustomDatasourceTablePreferences) => void,
] => {
  const { user } = useRisksmartUser();
  const storageKey = buildLocalStorageKey(STORAGE_KEY, user?.orgKey ?? null);

  const readScope = useCallback(
    (): CustomDatasourceTablePreferences | undefined =>
      scope ? readStore(storageKey)[scope] : undefined,
    [scope, storageKey]
  );

  const [preferences, setStoredPreferences] = useState(readScope);

  // Re-seed when the instance changes under a mounted table — routing between two
  // datasources reuses the component, and `orgKey` resolves after the first render.
  const [seed, setSeed] = useState({ scope, storageKey });
  if (seed.scope !== scope || seed.storageKey !== storageKey) {
    setSeed({ scope, storageKey });
    setStoredPreferences(readScope());
  }

  const setPreferences = useCallback(
    (updated: CustomDatasourceTablePreferences) => {
      setStoredPreferences(updated);
      if (!scope) {
        return;
      }
      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({ ...readStore(storageKey), [scope]: updated })
        );
      } catch (error) {
        handleError(error);
      }
    },
    [scope, storageKey]
  );

  return [preferences, setPreferences];
};
